'use client';

import type { ButtonHTMLAttributes } from 'react';

export type ButtonFit = 'block' | 'inline';

/**
 * Shared class list for the site's one primary (purple) button. Every "Join waitlist" / "Send
 * message" / "Contact Support" instance uses this, so they share one height scale — 56px, dropping
 * to 44px only below 380px (the narrowest phones) — instead of each section inventing its own (they
 * previously ranged from 36px up to 56px depending on where they sat: the header's own nav button,
 * the footer's, and each form's submit button all had different height ladders).
 *
 * Exported as a plain function, not just the `Button` component below, so a `<Link>` that must stay
 * an anchor for routing (the footer's "Contact Support") can use the exact same classes as an actual
 * `<button>` — same source of truth, same height, just a different tag where semantics require it.
 *
 *   fit="block"   fills its container (every form's submit button).
 *   fit="inline"  hugs its content plus horizontal padding (nav/footer CTAs, which sit inline with
 *                 other content rather than spanning a form's width).
 */
export function buttonClassName(fit: ButtonFit = 'block', className = ''): string {
  const width = fit === 'block' ? 'w-full' : 'w-auto px-6 max-[380px]:px-4';
  // ds-text-title-base: the DS's "title" text style at 16px (the site's floor — nothing renders
  // smaller) — every button's label, every breakpoint, matching the 16px unification applied to
  // every field.
  return `btn-primary inline-flex h-[56px] shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-12 ds-text-title-base text-white max-[380px]:h-[44px] max-[380px]:rounded-10 ${width} ${className}`;
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  fit?: ButtonFit;
  className?: string;
};

/** An actual `<button>` instance of the shared primary-button styling. */
export function Button({ fit = 'block', className = '', children, ...rest }: ButtonProps) {
  return (
    <button {...rest} className={buttonClassName(fit, className)}>
      {children}
    </button>
  );
}
